import { ProcessStepsList } from '@/components/processes/ProcessStepsList';
import type {
  ProcessFailureFrequency,
  ProcessRunFrequency,
  ProcessWithSteps,
} from '@/config/objects/processes';
import {
  Badge,
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  EmptyTableState,
  Loading,
  PageContainer,
  formatDate,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileText,
  ListChecks,
  Pencil,
  RefreshCw,
  Trash2,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const RUN_FREQUENCY_LABELS: Record<ProcessRunFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  ad_hoc: 'Ad-hoc',
};

const FAILURE_FREQUENCY_LABELS: Record<ProcessFailureFrequency, string> = {
  rarely: 'Rarely',
  sometimes: 'Sometimes',
  often: 'Often',
  always: 'Always',
};

function ownerName(owner: ProcessWithSteps['owner']): string {
  if (!owner) return 'Unassigned';
  const name = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
  return name || owner.email;
}

/**
 * Section card with title, description, and either content or an empty state.
 */
function NarrativeCard({
  title,
  description,
  content,
  icon: Icon,
}: {
  title: string;
  description: string;
  content: string | null | undefined;
  icon: typeof FileText;
}) {
  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="p-6">
        {content?.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{content}</p>
        ) : (
          <EmptyTableState
            icon={<Icon className="h-6 w-6" />}
            title="Not documented yet"
            description="Edit to add this detail"
          />
        )}
      </div>
    </div>
  );
}

/** Process detail page — displays the documented process and hosts the steps editor. */
export function ProcessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: ProcessWithSteps }>({
    queryKey: ['Process', id],
    queryFn: () => apiClient.get(`/processes/${id}`),
    enabled: !!id,
  });

  const process = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (processId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/processes/${processId}`);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Process'] });
        navigate('/processes');
      } else {
        throw new Error(response.error ?? 'Failed to delete process');
      }
    },
  });

  const handleDelete = () => {
    if (!process) return;
    toast.promise(deleteMutation.mutateAsync(process.id), {
      loading: 'Deleting process...',
      success: 'Process deleted successfully',
      error: (error: Error) => error?.message ?? 'Failed to delete process',
    });
  };

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', href: '/' },
      { label: 'Processes', href: '/processes' },
      { label: process?.title ?? 'Process', current: true },
    ],
    [process?.title],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Process Not Found</h2>
          <p className="text-muted-foreground">The requested process could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const failureFreqLabel = process?.failureFrequency
    ? FAILURE_FREQUENCY_LABELS[process.failureFrequency]
    : null;
  const failureFreqClass = (() => {
    switch (process?.failureFrequency) {
      case 'always':
      case 'often':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'sometimes':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'rarely':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return '';
    }
  })();

  return (
    <PageContainer padding="none" className="flex flex-col min-h-full">
      <DetailPageHeader
        actions={
          process && (
            <>
              <Button variant="outline" onClick={() => navigate(`/processes/${id}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : (
        <DetailPageHero
          isLoading={false}
          category={{ label: 'Process', icon: ClipboardList }}
          title={process?.title ?? 'Process'}
          meta={
            <>
              {process?.owner && (
                <span className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {ownerName(process.owner)}
                </span>
              )}
              {process?.runFrequency && (
                <span className="flex items-center gap-1">
                  <RefreshCw className="h-3.5 w-3.5" />
                  {RUN_FREQUENCY_LABELS[process.runFrequency]}
                </span>
              )}
              {process?.estimatedMinutes != null && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  ~{process.estimatedMinutes} min/run
                </span>
              )}
            </>
          }
          badges={
            failureFreqLabel && (
              <Badge variant="outline" className={failureFreqClass}>
                <AlertCircle className="h-3 w-3 mr-1" />
                Fails: {failureFreqLabel}
              </Badge>
            )
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left — narrative */}
            <div className="lg:col-span-3 space-y-6">
              <NarrativeCard
                title="What is the process?"
                description="A short summary of the work"
                content={process?.description}
                icon={FileText}
              />
              <NarrativeCard
                title="Why this matters"
                description="The outcome this produces"
                content={process?.whyItMatters}
                icon={FileText}
              />
              <NarrativeCard
                title="What success looks like"
                description="How you know the process worked"
                content={process?.successCriteria}
                icon={CheckCircle2}
              />
              <NarrativeCard
                title="What failure looks like"
                description="How you know the process went wrong"
                content={process?.failureModes}
                icon={AlertCircle}
              />
            </div>

            {/* Right — metadata */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-card shadow-sm">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-medium tracking-tight text-zinc-900">Details</h2>
                </div>
                <div className="p-6">
                  <div className="divide-y">
                    <div className="py-3 first:pt-0">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Owner
                      </div>
                      <div className="text-sm">{ownerName(process?.owner)}</div>
                    </div>
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Run frequency
                      </div>
                      <div className="text-sm">
                        {process?.runFrequency
                          ? RUN_FREQUENCY_LABELS[process.runFrequency]
                          : <span className="text-muted-foreground italic">Not set</span>}
                      </div>
                    </div>
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Failure frequency
                      </div>
                      <div className="text-sm">
                        {failureFreqLabel ?? (
                          <span className="text-muted-foreground italic">Not set</span>
                        )}
                      </div>
                    </div>
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Estimated minutes per run
                      </div>
                      <div className="text-sm">
                        {process?.estimatedMinutes != null ? (
                          `${process.estimatedMinutes} min`
                        ) : (
                          <span className="text-muted-foreground italic">Not set</span>
                        )}
                      </div>
                    </div>
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Last updated
                      </div>
                      <div className="text-sm">{formatDate(process?.updatedAt)}</div>
                    </div>
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        Created
                      </div>
                      <div className="text-sm">{formatDate(process?.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Steps — full width */}
          <div className="rounded-xl border bg-card shadow-sm mt-6">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Steps</h2>
              <p className="text-sm text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5 inline mr-1" />
                Ordered steps in this process. Drag to reorder, click any step to edit.
              </p>
            </div>
            <div className="p-6">
              {process && (
                <ProcessStepsList processId={process.id} steps={process.steps ?? []} />
              )}
            </div>
          </div>
        </DetailPageHero>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Process"
        description={`Are you sure you want to delete "${process?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
